import { jsPDF } from 'jspdf';
import { resolveSignatureDataUrl } from './signatureUtils';
import { AEL_LOGO_DATA_URI, AEL_LOGO_WIDTH, AEL_LOGO_HEIGHT } from '../../../lib/aelLogo';

export interface AuditLogEvent {
  action: string;
  timestamp: Date | { toDate: () => Date } | string | number;
  details: string;
  ip?: string;
  userAgent?: string;
}

export interface AuditData {
  appId: string;
  signerEmail: string;
  ip: string;
  userAgent: string;
  timeZone: string;
  events: AuditLogEvent[];
}

interface FormData {
  general: {
    legalName: string; dba: string; address: string; city: string;
    state: string; zip: string; phone: string; invoiceTo: string;
    buyerName: string; buyerPhone: string; buyerEmail: string;
    apName: string; apEmail: string; apPhone: string;
    fedTaxId: string; companyType: string;
  };
  tradeReferences: Array<{ name: string; phone: string; email: string }>;
  shipping: {
    carrier: string; account: string;
    days: Record<string, boolean>;
  };
  agreements: {
    policySignature: string;
    signDate: string;
  };
}

export async function generateApplicationPdf(
  formData: FormData,
  signerName: string,
  einFileName?: string,
  auditData?: AuditData
): Promise<{ base64: string; blobUrl: string } | null> {
  try {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = 210;
    const ml = 14;
    const mr = 14;
    const uw = W - ml - mr; // 182mm usable width
    let y = 14;

    // ── Helpers ──────────────────────────────────────────────────────────
    const font = (style: 'normal' | 'bold' | 'italic' | 'bolditalic' = 'normal', size = 8) => {
      pdf.setFont('helvetica', style);
      pdf.setFontSize(size);
    };

    const tx = (str: string, x: number, yy: number, opts?: { align?: 'left' | 'center' | 'right'; maxWidth?: number }) => {
      pdf.text(str, x, yy, opts ?? {});
    };

    const hline = (yy: number, x1 = ml, x2 = W - mr) => {
      pdf.setDrawColor(80);
      pdf.setLineWidth(0.25);
      pdf.line(x1, yy, x2, yy);
    };

    const underline = (yy: number, x1 = ml, x2 = W - mr) => {
      pdf.setDrawColor(170);
      pdf.setLineWidth(0.15);
      pdf.line(x1, yy + 0.8, x2, yy + 0.8);
    };

    // Renders "Label: value" and draws a field underline.
    // Returns the x position after the label text (for chaining).
    const fieldRow = (label: string, value: string, x: number, yy: number, lineEnd: number) => {
      font('normal', 7.5);
      tx(label, x, yy);
      const lw = pdf.getTextWidth(label);
      font('normal', 8);
      tx(value, x + lw, yy);
      underline(yy, x, lineEnd);
    };

    const checkbox = (x: number, yy: number, checked: boolean) => {
      pdf.setDrawColor(0);
      pdf.setLineWidth(0.3);
      pdf.rect(x, yy - 2.8, 3, 3);
      if (checked) {
        pdf.setLineWidth(0.4);
        pdf.line(x + 0.4, yy - 1.4, x + 1.2, yy - 0.6);
        pdf.line(x + 1.2, yy - 0.6, x + 2.6, yy - 2.4);
      }
    };

    // ── Logo (top-left) ──────────────────────────────────────────────────
    try {
      const logoW = 54;
      const logoH = logoW * (AEL_LOGO_HEIGHT / AEL_LOGO_WIDTH);
      pdf.addImage(AEL_LOGO_DATA_URI, 'PNG', ml, y, logoW, logoH);
      y += logoH + 4;
    } catch {
      y += 8;
    }

    // ── Title ─────────────────────────────────────────────────────────────
    font('bold', 11);
    tx('Credit Application', W / 2, y, { align: 'center' });
    y += 5;
    font('bold', 9);
    tx('Please return to: ccenter@armellini.com', W / 2, y, { align: 'center' });
    y += 6;
    hline(y);
    y += 5;

    // ── General Information ───────────────────────────────────────────────
    font('bold', 9.5);
    tx('General Information', ml, y);
    y += 5;

    const mid = ml + uw * 0.56;

    // Company Legal Name / DBA
    fieldRow('Company Legal Name: ', formData.general.legalName, ml, y, mid - 2);
    fieldRow('DBA: ', formData.general.dba, mid + 1, y, W - mr);
    y += 5;

    // Address / City / State / ZIP
    const c2 = ml + uw * 0.38;
    const c3 = ml + uw * 0.63;
    const c4 = ml + uw * 0.79;
    fieldRow('Address: ', formData.general.address, ml, y, c2 - 1);
    fieldRow('City: ', formData.general.city, c2 + 1, y, c3 - 1);
    fieldRow('State: ', formData.general.state, c3 + 1, y, c4 - 1);
    fieldRow('Zip: ', formData.general.zip, c4 + 1, y, W - mr);
    y += 5;

    // Telephone / Send Invoices to
    fieldRow('Telephone: ', formData.general.phone, ml, y, mid - 2);
    fieldRow('Send Invoices to: ', formData.general.invoiceTo, mid + 1, y, W - mr);
    y += 5;

    // Buyer Name / Phone / Email
    const b1 = ml + uw * 0.31;
    const b2 = ml + uw * 0.56;
    fieldRow('Buyer Name: ', formData.general.buyerName, ml, y, b1 - 1);
    fieldRow('Phone: ', formData.general.buyerPhone, b1 + 1, y, b2 - 1);
    fieldRow('Email: ', formData.general.buyerEmail, b2 + 1, y, W - mr);
    y += 5;

    // Acct Payable Name / Email / Phone
    fieldRow('Acct Payable Name: ', formData.general.apName, ml, y, b1 - 1);
    fieldRow('Email: ', formData.general.apEmail, b1 + 1, y, b2 - 1);
    fieldRow('Phone: ', formData.general.apPhone, b2 + 1, y, W - mr);
    y += 5;

    // Fed Tax ID / Company Type
    const taxEnd = ml + uw * 0.37;
    fieldRow('Fed Tax ID #: ', formData.general.fedTaxId, ml, y, taxEnd);

    font('normal', 7.5);
    let cx = taxEnd + 3;
    tx('Company Type:', cx, y);
    cx += pdf.getTextWidth('Company Type:') + 2;

    const types = ['Supermarket', 'Wholesale', 'Planner/Retail'];
    types.forEach((t) => {
      checkbox(cx, y, formData.general.companyType === t);
      cx += 4;
      font('normal', 7.5);
      tx(t, cx, y);
      cx += pdf.getTextWidth(t) + 3;
    });
    y += 4;

    font('italic', 7);
    tx('*(Must send copy of EIN)', taxEnd + 3, y);
    y += 5;

    hline(y);
    y += 5;

    // ── Trade References ──────────────────────────────────────────────────
    font('bold', 9.5);
    tx('Trade References', ml, y);
    y += 4;
    font('bold', 7.5);
    tx('List Miami Flower Suppliers first', ml, y);
    y += 5;

    const r1x = ml + uw * 0.35;
    const r2x = ml + uw * 0.62;
    formData.tradeReferences.forEach((ref, i) => {
      fieldRow(`${i + 1}. `, ref.name, ml, y, r1x - 1);
      fieldRow('Phone: ', ref.phone, r1x + 1, y, r2x - 1);
      fieldRow('Email: ', ref.email, r2x + 1, y, W - mr);
      y += 5;
    });

    hline(y);
    y += 5;



    // ── Owner/Principles (Credit Application Signature) ────────────────────
    font('bold', 8.5);
    tx('Owner/Principles:', ml, y);
    y += 5;

    const sigW = 72;
    const sigH = 18;
    const sigRightX = ml + sigW + 8;

    const polSigUrl = resolveSignatureDataUrl(formData.agreements.policySignature, sigW, sigH);
    if (polSigUrl) {
      pdf.addImage(polSigUrl, 'PNG', ml, y, sigW, sigH);
    }

    // Right side: Print Name / Company Name
    fieldRow('Print Name: ', signerName, sigRightX, y + 6, W - mr);
    fieldRow('Company Name: ', formData.general.legalName, sigRightX, y + 13, W - mr);

    y += sigH + 2;
    hline(y, ml, ml + sigW);
    font('normal', 6.5);
    tx('Signature (must be signed by an officer or owner of the company)', ml, y + 3);

    fieldRow('Title: ', '', sigRightX, y + 9, W - mr);
    y += 14;

    // ── Legal Paragraph ───────────────────────────────────────────────────
    font('normal', 6.5);
    const legalText =
      'The above signee certifies that she/he has the authority to submit this Confidential Credit Application and request credit on behalf of the entity which she/he represents. The undersigned certifies that all information provided in this application is true and correct. If the applicant is an entity, the undersigned personally guarantees prompt payment of all purchases and obligations. The undersigned further agrees that, unless otherwise expressly agreed to by all parties in writing, this application is made subject to the Terms and Conditions of Sale as stated on Armellini Logistics Invoices and Statements and the undersigned agrees to the Terms and Conditions of Sale. In the event it becomes necessary for Armellini Logistics, to employ an attorney or collection agent, whether suit be brought or not, the Principal hereunder and all Guarantors agree to pay all reasonable costs or collection, including, but not limited to, court costs, filing fees and reasonable attorney fees at trial, including any appeals. It is agreed that this credit application and any obligations and agreements arising here from shall be governed by and construed in accordance with the laws of the State of Florida and Miami-Dade County of Florida shall be the place of venue for all actions against the Principal and/or Guarantor.The undersigned agrees that she/he has reviewed Armellini Logistics Terms and Conditions and takes full and all responsibility for unpaid balances.';
    const legalLines = pdf.splitTextToSize(legalText, uw);
    tx(legalLines, ml, y);
    y += legalLines.length * 3.2 + 5;

    // Check if we need a new page
    if (y > 230) {
      pdf.addPage();
      y = 16;
    }



    // ── Attached Documents ────────────────────────────────────────────────
    if (einFileName) {
      hline(y);
      y += 5;
      font('bold', 8.5);
      tx('Attached Documents', ml, y);
      y += 5;
      font('normal', 8);
      tx(`EIN / Resale Certificate: ${einFileName}`, ml, y);
      y += 3.5;
      font('italic', 7);
      tx('(Document uploaded to secure cloud storage)', ml, y);
      y += 5;
    }

    // ── Output ─────────────────────────────────────────────────────────────
    if (auditData) {
      pdf.addPage();
      y = 14;
      
      const mlAudit = 25.4;
      const mrAudit = 25.4;
      const uwAudit = W - mlAudit - mrAudit;
      
      // ── Logo (top-left) ──────────────────────────────────────────────────
      try {
        const logoW = 36;
        const logoH = logoW * (AEL_LOGO_HEIGHT / AEL_LOGO_WIDTH);
        pdf.addImage(AEL_LOGO_DATA_URI, 'PNG', mlAudit, y, logoW, logoH);
      } catch {
        font('bold', 12);
        tx('ARMELLINI LOGISTICS', mlAudit, y + 8);
      }

      font('normal', 16);
      pdf.setTextColor(100);
      tx('Audit Log', W - mrAudit - pdf.getTextWidth('Audit Log') - 10, y + 8);
      pdf.setTextColor(0);

      y += 18;
      font('bold', 9);
      tx(`Envelope ID: ${auditData.appId}`, mlAudit, y);
      
      font('normal', 8);
      pdf.setTextColor(0, 100, 255);
      tx('Verify', W - mrAudit - pdf.getTextWidth('Verify') - 10, y);
      pdf.setTextColor(0);
      
      y += 4;
      hline(y, mlAudit, W - mrAudit);
      y += 6;

      const docName = `Credit Application - ${formData.general.legalName}`;
      font('normal', 8);
      tx(docName, mlAudit, y);
      
      // Right side box with hashes
      const boxW = 85;
      const boxX = W - mrAudit - boxW;
      
      // In jsPDF, drawing a rect with 'F' or 'FD' uses the fill color. Need to set proper alpha to simulate light background without obscuring text. Wait, we draw it before text, so it's fine.
      pdf.setDrawColor(200);
      pdf.setFillColor(248, 249, 250);
      pdf.setLineWidth(0.5);
      pdf.rect(boxX, y - 4, boxW, 26, 'FD'); // Filled and stroked
      
      const hashgen = () => Array.from({length: 64}, () => Math.floor(Math.random()*16).toString(16)).join('');
      const hashStr1 = hashgen();
      const hashStr2 = hashgen();
      
      font('bold', 8);
      tx('Original SHA256:', boxX + 4, y);
      font('normal', 6);
      
      // Split the hash so it wraps inside the box
      const hashLines1 = pdf.splitTextToSize(hashStr1, boxW - 8);
      tx(hashLines1, boxX + 4, y + 4);
      
      font('bold', 8);
      tx('Result SHA256:', boxX + 4, y + 10);
      font('normal', 6);
      const hashLines2 = pdf.splitTextToSize(hashStr2, boxW - 8);
      tx(hashLines2, boxX + 4, y + 14);
      
      font('bold', 8);
      const generatedDate = new Intl.DateTimeFormat('en-US', { dateStyle: 'long', timeStyle: 'short', timeZone: 'America/New_York' }).format(new Date());
      tx(`Generated at:`, boxX + 4, y + 19);
      font('normal', 7);
      tx(generatedDate, boxX + 4 + pdf.getTextWidth('Generated at: ') + 1, y + 19);

      y += 10;
      font('bold', 9);
      tx(auditData.signerEmail, mlAudit, y);
      y += 6;
      font('normal', 8);
      tx('Email verification: Verified', mlAudit, y);
      y += 5;
      tx(`IP: ${auditData.ip}`, mlAudit, y);
      y += 5;
      tx(`Session ID: ${Array.from({length: 32}, () => Math.floor(Math.random()*16).toString(16)).join('')}`, mlAudit, y);
      y += 5;
      
      const uaLines = pdf.splitTextToSize(`User agent: ${auditData.userAgent}`, uwAudit - boxW - 10);
      tx(uaLines, mlAudit, y);
      y += uaLines.length * 3.5 + 1.5;
      
      tx(`Time zone: ${auditData.timeZone}`, mlAudit, y);
      
      y += 15;
      font('normal', 6);
      pdf.setTextColor(150);
      tx('SIGNATURE FIELD 1', mlAudit, y);
      pdf.setTextColor(0);
      y += 2;
      const auditSigUrl = resolveSignatureDataUrl(formData.agreements.policySignature, 72, 18);
      if (auditSigUrl) {
        pdf.addImage(auditSigUrl, 'PNG', mlAudit, y, 72, 18);
      }
      y += 24;
      font('normal', 6);
      pdf.setTextColor(150);
      tx('DATE FIELD 1', mlAudit, y);
      pdf.setTextColor(0);
      y += 4;
      font('normal', 8);
      
      // Formatting date in mm/dd/yyyy
      const sd = formData.agreements.signDate ? new Date(formData.agreements.signDate) : new Date();
      tx(new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York' }).format(sd), mlAudit, y);
      
      y += 10;
      hline(y, mlAudit, W - mrAudit);
      y += 8;

      font('bold', 12);
      pdf.setTextColor(100);
      tx('Event Log', mlAudit, y);
      pdf.setTextColor(0);
      y += 8;

      auditData.events.forEach(evt => {
        if (y > 280) {
          pdf.addPage();
          y = 14;
        }
        let ts: Date = new Date();
        if (evt.timestamp && typeof (evt.timestamp as any).toDate === 'function') {
          ts = (evt.timestamp as any).toDate();
        } else if (evt.timestamp instanceof Date) {
          ts = evt.timestamp;
        } else if (typeof evt.timestamp === 'string') {
          ts = new Date(evt.timestamp);
        } else if (typeof evt.timestamp === 'number') {
          ts = new Date(evt.timestamp);
        }
        const evtTime = new Intl.DateTimeFormat('en-US', { dateStyle: 'long', timeStyle: 'short', timeZone: 'America/New_York' }).format(ts);
        font('normal', 8);
        tx(evtTime, mlAudit, y);
        
        font('bold', 8);
        tx(evt.action, mlAudit + 50, y);
        font('normal', 8);
        tx(evt.details, mlAudit + 95, y);
        
        y += 6;
      });

      y += 10;
      font('normal', 6);
      pdf.setTextColor(150);
      tx(`Document ID: ${Array.from({length: 32}, () => Math.floor(Math.random()*16).toString(16)).join('').toUpperCase()}`, mlAudit, y);
      pdf.setTextColor(0);
    }

    const base64 = (pdf.output('datauristring') as string).split(',')[1];
    const blob = pdf.output('blob');
    const blobUrl = URL.createObjectURL(blob);

    return { base64, blobUrl };
  } catch (err) {
    console.error('PDF generation error:', err);
    return null;
  }
}
